/**
 * 翻译练习界面
 * 独立的卡片式翻译练习界面
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { incrementGuestPracticeCount } from '../store/authSlice';
import backendAI from '../services/ai';
import llmFramework, { EnhancedTranslationEvaluation } from '../services/llmFramework';
import vocabularyApiService, { VocabularyApiResponse } from '../services/vocabularyApiService';
import { practiceMemoryService } from '../services/practiceMemoryService';

interface TranslationExercise {
  id: string;
  chineseText: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
  context?: string; // 从对话中提取的上下文
}

interface AIEvaluation {
  score: number; // 0-100分
  isCorrect: boolean;
  feedback: string;
  suggestions: string[];
  referenceTranslations: string[];
}

// 从对话中生成练习句子的函数
const generateExercisesFromConversation = (messages: any[]): TranslationExercise[] => {
  console.log('=== 开始生成练习 ===');
  console.log('总消息数:', messages.length);

  // 首先确保返回默认练习作为基础
  const defaultExercises = getDefaultExercises();

  // 如果没有消息，直接返回默认练习
  if (!messages || messages.length === 0) {
    console.log('没有消息，使用默认练习');
    return defaultExercises;
  }

  // 打印所有消息详情以便调试
  console.log('所有消息详情:');
  messages.forEach((msg, index) => {
    console.log(`  ${index + 1}. "${msg.text}" (isUser: ${msg.isUser}, 长度: ${msg.text?.length || 0})`);
  });

  // 获取用户的消息内容 - 优先使用isUser标记，辅以智能识别
  console.log('\n--- 开始过滤用户消息 ---');

  const potentialUserMessages = messages.filter((msg, index) => {
    if (!msg || !msg.text) {
      console.log(`  ${index + 1}. 跳过：空消息`);
      return false;
    }

    const text = msg.text.trim();

    // 主要依赖 isUser 标记，这是最可靠的判断方式
    if (msg.isUser === true) {
      console.log(`  ${index + 1}. ✓ 确认用户消息: "${text}"`);
      return true;
    }

    // 如果明确标记为AI消息，直接排除
    if (msg.isUser === false) {
      console.log(`  ${index + 1}. ✗ 确认AI消息: "${text.substring(0, 30)}..."`);
      return false;
    }

    // 对于没有明确标记的消息，保守处理：只排除明显的AI回复模式
    console.log(`  ${index + 1}. ✗ 没有明确用户标记: "${text.substring(0, 30)}..."`);
    return false;
  }).slice(-15); // 取更多最近的消息来分析

  console.log('\n--- 用户消息过滤结果 ---');
  console.log('筛选出的用户消息数:', potentialUserMessages.length);
  potentialUserMessages.forEach((msg, i) => {
    console.log(`  ${i + 1}. "${msg.text}" (isUser: ${msg.isUser}, 长度: ${msg.text.length})`);
  });

  const exercises: TranslationExercise[] = [];

  // 从用户消息中提取关键句子并生成翻译练习
  console.log('\n--- 开始生成练习题目 ---');
  potentialUserMessages.forEach((msg, index) => {
    const text = msg.text.trim();
    console.log(`检查消息 ${index + 1}: "${text}" (长度: ${text.length})`);

    // 适当的长度限制，保证练习质量
    if (text.length >= 2 && text.length <= 50 && exercises.length < 3) {
      exercises.push({
        id: `dynamic_${index + 1}`,
        chineseText: text,
        difficulty: text.length <= 15 ? 'easy' : text.length <= 30 ? 'medium' : 'hard',
        category: '对话练习',
        context: '这句话来自你之前的对话内容',
      });
      console.log(`✓ 添加练习: "${text}" (雾度: ${text.length <= 15 ? '简单' : text.length <= 30 ? '中等' : '困雾'})`);
    } else {
      console.log(`✗ 跳过: "${text}" (长度: ${text.length}, 已有练习: ${exercises.length})`);
    }
  });

  // 如果没有从对话中生成足够的练习，使用默认练习
  const finalExercises = [...exercises];

  // 补充默认练习到3个（添加安全计数器防止无限循环）
  let attemptCount = 0;
  const maxAttempts = 10; // 防止无限循环的安全措施
  
  while (finalExercises.length < 3 && attemptCount < maxAttempts) {
    attemptCount++;
    console.log(`补充练习尝试 #${attemptCount}, 当前练习数: ${finalExercises.length}`);
    
    // 检查默认练习是否存在
    if (!defaultExercises || defaultExercises.length === 0) {
      console.error('❌ 默认练习为空，无法补充练习');
      break;
    }
    
    // 随机选择默认练习，避免总是相同的题目
    const availableDefaults = defaultExercises.filter(def =>
      !finalExercises.some(ex => ex.id === def.id)
    );

    if (availableDefaults.length > 0) {
      const randomDefault = availableDefaults[Math.floor(Math.random() * availableDefaults.length)];
      finalExercises.push(randomDefault);
      console.log(`✅ 添加随机默认练习: "${randomDefault.chineseText}"`);
    } else {
      // 如果没有更多默认练习，重复使用现有的
      const randomIndex = Math.floor(Math.random() * defaultExercises.length);
      const duplicateEx = {
        ...defaultExercises[randomIndex],
        id: `duplicate_${finalExercises.length}_${Date.now()}`, // 添加时间戳确保唯一性
      };
      finalExercises.push(duplicateEx);
      console.log(`✅ 添加重复练习: "${duplicateEx.chineseText}" (ID: ${duplicateEx.id})`);
    }
  }
  
  // 如果达到最大尝试次数，记录警告
  if (attemptCount >= maxAttempts) {
    console.warn(`⚠️ 达到最大尝试次数 (${maxAttempts})，停止补充练习`);
  }

  console.log('\n--- 最终练习结果 ---');
  console.log('最终练习列表:');
  finalExercises.forEach((ex, i) => {
    console.log(`  ${i + 1}. "${ex.chineseText}" (类别: ${ex.category}, ID: ${ex.id})`);
  });

  // 补充信息
  const userMessageCount = finalExercises.filter(ex => ex.category === '对话练习').length;
  const defaultMessageCount = finalExercises.filter(ex => ex.category !== '对话练习').length;
  console.log(`\n练习组成: ${userMessageCount}个对话练习 + ${defaultMessageCount}个默认练习 = ${finalExercises.length}个总计`);
  console.log('=== 练习生成完成 ===\n');

  return finalExercises;
};

// 生成英文翻译（使用更好的翻译映射）
const generateEnglishTranslation = (chineseText: string): string => {
  // 改进的翻译映射，提供更准确的翻译
  const translations: { [key: string]: string } = {
    '我今天过得很愉快': 'I had a great day today',
    '我今天很开心': 'I am very happy today',
    '我今天很累': 'I am very tired today',
    '今天天气很好': 'The weather is nice today',
    '我喜欢学习英语': 'I like learning English',
    '这本书很有趣': 'This book is very interesting',
    '我想去公园散步': 'I want to go for a walk in the park',
    '我和朋友聊了很多有趣的话题': 'I talked about many interesting topics with my friends',
    '我觉得学习新技能很有意思': 'I find learning new skills very interesting',
    '工作很忙': 'Work is very busy',
    '学习很有趣': 'Studying is very interesting',
    '我很喜欢这个地方': 'I really like this place',
    '今天是个好日子': 'Today is a good day',
    '我需要休息一下': 'I need to take a break',
    '这个问题很难': 'This problem is very difficult',
    '我们一起努力': 'Let\'s work together',
    // 添加更多常见的中文句子翻译
    '我觉得现在体验好多了': 'I feel much better now',
    '我觉得这个很不错': 'I think this is very good',
    '我觉得体验很好': 'I think the experience is very good',
    '现在体验好多了': 'The experience is much better now',
    '体验好多了': 'The experience is much better',
    '我感觉好多了': 'I feel much better',
    '现在感觉好多了': 'I feel much better now',
    '我觉得不错': 'I think it\'s good',
    '体验很棒': 'The experience is great',
    '感觉很好': 'It feels great',
    '我很满意': 'I am very satisfied',
    '效果很好': 'It works very well',
    '用起来很方便': 'It\'s very convenient to use',
    '这个功能很实用': 'This feature is very practical',
    '界面很简洁': 'The interface is very clean',
    '操作很简单': 'The operation is very simple',
  };

  // 直接查找完整匹配
  if (translations[chineseText]) {
    return translations[chineseText];
  }

  // 如果没有完整匹配，尝试生成基于关键词的翻译
  const keywordTranslations: { [key: string]: string } = {
    '我': 'I',
    '今天': 'today',
    '很': 'very',
    '开心': 'happy',
    '愉快': 'pleasant/happy',
    '累': 'tired',
    '天气': 'weather',
    '好': 'good/nice',
    '喜欢': 'like',
    '学习': 'study/learn',
    '英语': 'English',
    '书': 'book',
    '有趣': 'interesting',
    '想': 'want to',
    '公园': 'park',
    '散步': 'take a walk',
    '朋友': 'friends',
    '聊': 'talk/chat',
    '话题': 'topics',
    '觉得': 'think/feel',
    '技能': 'skills',
    '工作': 'work',
    '忙': 'busy',
    '地方': 'place',
    '日子': 'day',
    '需要': 'need',
    '休息': 'rest/break',
    '问题': 'problem',
    '难': 'difficult',
    '努力': 'work hard',
  };

  // 构建基于关键词的翻译提示
  let translationHints: string[] = [];
  for (const [chinese, english] of Object.entries(keywordTranslations)) {
    if (chineseText.includes(chinese)) {
      translationHints.push(`"${chinese}" = ${english}`);
    }
  }

  if (translationHints.length > 0) {
    // 使用关键词组合生成更自然的翻译建议
    const keywordBasedTranslation = generateKeywordBasedTranslation(chineseText, translationHints);
    if (keywordBasedTranslation) {
      return keywordBasedTranslation;
    }
  }

  // 如果都没有匹配到，返回一个通用的英文翻译
  return generateBasicTranslation(chineseText);
};

// 基于关键词组合生成翻译
const generateKeywordBasedTranslation = (chineseText: string, translationHints: string[]): string => {
  // 尝试根据关键词和句子结构生成更合理的翻译
  const text = chineseText.toLowerCase();
  
  // 改进的模式匹配，提供更完整的翻译而不是模板
  if (chineseText.includes('我今天')) {
    if (chineseText.includes('很开心') || chineseText.includes('快乐')) {
      return 'I was very happy today';
    } else if (chineseText.includes('很累')) {
      return 'I was very tired today';
    } else if (chineseText.includes('过得')) {
      return 'I had a great day today';
    }
    return 'Today I did something'; // 更自然的默认翻译
  }
  
  if (chineseText.includes('我和朋友')) {
    if (chineseText.includes('聊')) {
      return 'I talked with my friends';
    }
    return 'My friends and I spent time together';
  }
  
  if (chineseText.includes('我觉得')) {
    if (chineseText.includes('学习')) {
      return 'I think learning is interesting';
    } else if (chineseText.includes('体验')) {
      return 'I think the experience is good';
    } else if (chineseText.includes('好')) {
      return 'I think it is good';
    } else if (chineseText.includes('不错')) {
      return 'I think it is pretty good';
    }
    return 'I think it is fine';
  }
  
  if (chineseText.includes('今天天气')) {
    if (chineseText.includes('好') || chineseText.includes('不错')) {
      return 'The weather is nice today';
    }
    return 'The weather today is okay';
  }
  
  if (chineseText.includes('我想')) {
    if (chineseText.includes('去')) {
      return 'I want to go somewhere';
    }
    return 'I want to do something';
  }
  
  if (chineseText.includes('工作')) {
    if (chineseText.includes('忙')) {
      return 'Work is very busy';
    } else if (chineseText.includes('累')) {
      return 'Work is tiring';
    }
    return 'Work is going well';
  }
  
  // 通用模式处理
  if (chineseText.includes('体验')) {
    if (chineseText.includes('好') || chineseText.includes('棒')) {
      return 'The experience is great';
    } else if (chineseText.includes('不错')) {
      return 'The experience is pretty good';
    }
    return 'The experience is okay';
  }
  
  if (chineseText.includes('感觉')) {
    if (chineseText.includes('好')) {
      return 'It feels good';
    } else if (chineseText.includes('不错')) {
      return 'It feels pretty good';
    }
    return 'It feels okay';
  }
  
  // 如果没有匹配到具体模式，返回null让后面的函数处理
  return null;
};

// 生成基本翻译（当没有预设翻译时）
const generateBasicTranslation = (chineseText: string): string => {
  // 根据常见句型给出更自然的翻译而不是模板
  if (chineseText.startsWith('我')) {
    if (chineseText.includes('很') || chineseText.includes('非常')) {
      return 'I feel very good about it';
    } else if (chineseText.includes('想') || chineseText.includes('要')) {
      return 'I want to do it';
    } else if (chineseText.includes('喜欢')) {
      return 'I like it very much';
    } else if (chineseText.includes('觉得')) {
      return 'I think it is good';
    } else if (chineseText.includes('认为')) {
      return 'I believe it is correct';
    }
    return 'I think it is fine';
  } else if (chineseText.includes('今天')) {
    if (chineseText.includes('天气')) {
      return 'Today the weather is good';
    } else if (chineseText.includes('心情')) {
      return 'Today I feel good';
    }
    return 'Today is a good day';
  } else if (chineseText.includes('这个') || chineseText.includes('这本')) {
    if (chineseText.includes('书')) {
      return 'This book is interesting';
    } else if (chineseText.includes('地方')) {
      return 'This place is nice';
    } else if (chineseText.includes('问题')) {
      return 'This problem is challenging';
    }
    return 'This is very good';
  } else if (chineseText.includes('现在')) {
    return 'Now everything is better';
  } else if (chineseText.includes('可以')) {
    return 'It can be done well';
  } else if (chineseText.includes('应该')) {
    return 'It should be good';
  }

  // 最后的通用翻译，尽量避免使用原文
  return 'It is very good';
};

// 生成替代答案
const generateAlternatives = (chineseText: string): string[] => {
  // 根据中文内容生成多种英文表达
  const alternatives: string[] = [];

  if (chineseText.includes('我今天')) {
    if (chineseText.includes('开心') || chineseText.includes('快乐')) {
      alternatives.push('I was happy today', 'I felt joyful today');
    } else if (chineseText.includes('累')) {
      alternatives.push('I was tired today', 'I felt exhausted today');
    } else if (chineseText.includes('过得') && chineseText.includes('快乐')) {
      alternatives.push('I had a wonderful day today', 'Today was a great day for me');
    }
  } else if (chineseText.includes('朋友') && chineseText.includes('聊')) {
    alternatives.push('I chatted with my friends', 'I had a conversation with friends');
  } else if (chineseText.includes('学习') && chineseText.includes('觉得')) {
    alternatives.push('I think learning is interesting', 'I find studying fascinating');
  } else if (chineseText.includes('工作') && chineseText.includes('忙')) {
    alternatives.push('Work is very busy', 'My job keeps me very busy');
  } else if (chineseText.includes('今天天气')) {
    alternatives.push('The weather is nice today', 'Today has beautiful weather');
  } else if (chineseText.includes('我想') && chineseText.includes('散步')) {
    alternatives.push('I want to go for a walk', 'I would like to take a walk');
  } else {
    // 如果没有匹配到具体模式，返回空数组
    // 这样在后面的代码中会使用主要翻译作为参考
  }

  return alternatives.slice(0, 2);
};

// 从英文文本中提取重点词汇 - 完全使用后端API
const extractKeyVocabularyFromEnglish = async (englishTexts: string[]): Promise<string[]> => {
  try {
    console.log('🎯 开始提取重点词汇(后端API):', englishTexts);
    
    // 合并所有英文文本
    const combinedText = englishTexts.join(' ');
    
    // 调用后端文本分析API
    const vocabularyData = await vocabularyApiService.analyzeTextVocabulary(combinedText);
    
    if (vocabularyData.length > 0) {
      console.log('✅ 后端文本分析成功:', vocabularyData.map(v => ({ 
        word: v.word, 
        phonetic: v.phonetic, 
        definition: v.definition 
      })));
      
      // 格式化为富文本显示格式
      const formattedVocabulary = vocabularyData.map(vocab => {
        // 音标数据调试日志（开发环境）
        console.log('🔍 词汇详情:', { 
          word: vocab.word, 
          phonetic: vocab.phonetic, 
          phoneticExists: !!vocab.phonetic,
          phoneticType: typeof vocab.phonetic
        });
        
        const phonetic = vocab.phonetic && vocab.phonetic.trim() !== '' 
          ? vocab.phonetic 
          : null; // 如果没有音标就不显示，不生成fallback
        const definition = vocab.definition || `${vocab.word}的含义`;
        const examples = vocab.examples && vocab.examples.length > 0 ? ` 例句: ${vocab.examples[0]}` : '';
        
        return `${vocab.word}${phonetic ? ` ${phonetic}` : ''} - ${definition}${examples}`;
      });
      
      console.log('📋 最终格式化的词汇列表:', formattedVocabulary);
      return formattedVocabulary;
    }
    
    console.log('⚠️ 后端API返回空结果');
    return [];
    
  } catch (error) {
    console.error('❌ 后端词汇分析失败:', error);
    return [];
  }
};

// 移除本地词汇提取，完全依赖后端API

// 移除本地定义字典，使用后端API

// 移除本地音标字典，使用后端API

// 生成学习提示
const generateTips = (chineseText: string): string[] => {
  const tips = ['注意语法结构', '考虑语境和时态'];

  if (chineseText.includes('我')) {tips.push('注意人称代词的使用');}
  if (chineseText.includes('今天') || chineseText.includes('昨天') || chineseText.includes('明天')) {
    tips.push('注意时间状语的表达');
  }
  if (chineseText.includes('很') || chineseText.includes('非常')) {
    tips.push('注意程度副词的翻译');
  }

  return tips.slice(0, 3);
};

// 默认练习题目（作为备选）
const getDefaultExercises = (): TranslationExercise[] => [
  {
    id: '1',
    chineseText: '我今天过得很愉快',
    difficulty: 'easy',
    category: '日常表达',
  },
  {
    id: '2',
    chineseText: '我和朋友聊了很多有趣的话题',
    difficulty: 'medium',
    category: '社交对话',
  },
  {
    id: '3',
    chineseText: '我觉得学习新技能很有意思',
    difficulty: 'medium',
    category: '学习体验',
  },
  {
    id: '4',
    chineseText: '今天天气很好',
    difficulty: 'easy',
    category: '日常表达',
  },
  {
    id: '5',
    chineseText: '我想去公园散步',
    difficulty: 'easy',
    category: '日常计划',
  },
  {
    id: '6',
    chineseText: '工作很忙但很有意义',
    difficulty: 'medium',
    category: '工作感受',
  },
];

interface TranslationPracticeScreenProps {
  onBackToChat: () => void;
  messages?: any[]; // 从ChatScreen传来的消息历史
}

const TranslationPracticeScreen: React.FC<TranslationPracticeScreenProps> = ({ onBackToChat, messages = [] }) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [completedExercises, setCompletedExercises] = useState(0);
  const [exercises, setExercises] = useState<TranslationExercise[]>([]);
  const [aiEvaluation, setAiEvaluation] = useState<AIEvaluation | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [reviewExercises, setReviewExercises] = useState<any[]>([]);
  const [isLoadingReview, setIsLoadingReview] = useState(false);

  const auth = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  // 初始化练习题目
  useEffect(() => {
    console.log('=== TranslationPracticeScreen 初始化 ===');
    console.log('接收到的messages总数:', messages.length);
    console.log('所有messages:', messages.map((m, i) => ({
      index: i,
      text: m.text?.substring(0, 30) + (m.text?.length > 30 ? '...' : ''),
      isUser: m.isUser,
      length: m.text?.length || 0,
    })));

    const generatedExercises = generateExercisesFromConversation(messages);
    console.log('最终生成的练习题目数量:', generatedExercises.length);
    console.log('练习内容:', generatedExercises.map(ex => ({
      id: ex.id,
      text: ex.chineseText,
      category: ex.category,
    })));

    setExercises(generatedExercises);

    // 确保总是有练习内容
    if (generatedExercises.length === 0) {
      console.log('⚠️ 没有生成练习，使用默认练习');
      setExercises(getDefaultExercises());
    }
  }, [messages]);

  const currentExercise = exercises[currentExerciseIndex];

  // 如果还没有练习题目，显示加载状态
  if (exercises.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>正在生成练习题目...</Text>
          <Text style={styles.loadingSubtext}>基于你的对话内容定制</Text>
          <Text style={styles.loadingSubtext}>接收到消息数: {messages.length}</Text>
          <Text style={styles.loadingSubtext}>用户消息数: {messages.filter(m => m.isUser).length}</Text>
        </View>
      </View>
    );
  }

  // 使用AI评估答案
  const checkAnswer = async () => {
    if (!userAnswer.trim()) {
      Alert.alert('提示', '请输入翻译内容');
      return;
    }

    if (!currentExercise) {return;}

    console.log('开始评估答案:', userAnswer);
    setIsEvaluating(true);

    try {
      const evaluation = await evaluateTranslationWithLLM(currentExercise.chineseText, userAnswer.trim());
      setAiEvaluation(evaluation);
      setShowResult(true);
      console.log('评估完成，分数:', evaluation.score);

      if (evaluation.score > 0) {
        setScore(prevScore => prevScore + evaluation.score);

        // 增加游客练习计数
        if (auth.isGuestMode) {
          dispatch(incrementGuestPracticeCount());
        }
      }

      setCompletedExercises(prev => prev + 1);
    } catch (error) {
      console.error('AI评估失败:', error);
      // 降级到简单评估
      const fallbackEvaluation = await simpleFallbackEvaluationWithAI(currentExercise.chineseText, userAnswer.trim());
      setAiEvaluation(fallbackEvaluation);
      setShowResult(true);
      setCompletedExercises(prev => prev + 1);
    } finally {
      console.log('评估结束，重置评估状态');
      setIsEvaluating(false);
    }
  };

  // 使用LLM框架进行翻译评估
  const evaluateTranslationWithLLM = async (chineseText: string, userTranslation: string): Promise<AIEvaluation> => {
    console.log('🚀 使用LLM框架进行翻译评估');
    
    try {
      const response = await llmFramework.callLLM<EnhancedTranslationEvaluation>({
        taskType: 'translation',
        input: userTranslation,
        context: chineseText,
        options: {
          level: 'intermediate',
          language: 'zh'
        }
      });

      if (response.success && response.data) {
        const enhancedEval = response.data;
        
        // 转换为原有的AIEvaluation格式，使用后端API数据
        const vocabularyTips = enhancedEval.vocabularyAnalysis.length > 0 
          ? enhancedEval.vocabularyAnalysis.map(v => {
              // 使用后端AI提供的词汇信息，不再依赖本地音标
              return `${v.word} - ${v.definition}`;
            })
          : [];
        
        const phraseTips = enhancedEval.phraseAnalysis.length > 0
          ? enhancedEval.phraseAnalysis.map(p => `"${p.phrase}" - ${p.meaning}`)
          : [];
          
        return {
          score: enhancedEval.score,
          isCorrect: enhancedEval.isCorrect,
          feedback: enhancedEval.feedback,
          suggestions: [
            ...vocabularyTips.slice(0, 3),
            ...phraseTips.slice(0, 2),
            ...enhancedEval.improvements.slice(0, 2)
          ].slice(0, 4), // 最多显示4个建议
          referenceTranslations: enhancedEval.referenceTranslations
        };
      }
    } catch (error) {
      console.error('LLM框架评估失败:', error);
    }
    
    // 降级到AI参考翻译评估
    return await simpleFallbackEvaluationWithAI(chineseText, userTranslation);
  };

  // 简单备选评估（当AI不可用时）
  // 使用LLM框架生成高质量参考翻译
  const generateReferenceTranslationWithAI = async (chineseText: string): Promise<string> => {
    try {
      console.log(`🚀 使用LLM框架生成参考翻译: "${chineseText}"`);
      
      const response = await llmFramework.callLLM<string>({
        taskType: 'translation',
        input: chineseText,
        options: {
          level: 'intermediate',
          language: 'zh'
        }
      });

      if (response.success && response.data) {
        console.log(`✅ LLM框架翻译成功: "${response.data}"`);
        return response.data;
      }
    } catch (error) {
      console.error('❌ LLM框架翻译失败:', error);
    }
    
    console.log('🔄 降级到前端翻译');
    return generateEnglishTranslation(chineseText);
  };

  // 带AI参考翻译的评估函数（异步）
  const simpleFallbackEvaluationWithAI = async (chineseText: string, userTranslation: string): Promise<AIEvaluation> => {
    const hasContent = userTranslation.length > 0;
    const isReasonableLength = userTranslation.length >= chineseText.length * 0.3;

    // 异步生成AI参考翻译
    const referenceTranslation = await generateReferenceTranslationWithAI(chineseText);

    let score = 0;
    if (hasContent && isReasonableLength) {
      score = 50; // 基础分数

      // 检查是否包含关键英文单词
      const userLower = userTranslation.toLowerCase();
      if (userLower.includes('i') || userLower.includes('you') || userLower.includes('is') || userLower.includes('are')) {
        score += 15; // 包含基本英文词汇
      }

      // 检查长度合理性
      if (userTranslation.length >= chineseText.length * 0.5) {
        score += 10; // 长度比较合理
      }

      // 检查与参考翻译的相似度
      if (userTranslation.toLowerCase().includes('today') && referenceTranslation.toLowerCase().includes('today')) {
        score += 10; // 关键词匹配
      }
    }

    // 限制最高分数
    score = Math.min(score, 85);

    // 获取重点词汇（从参考翻译中提取）
    const keyVocabulary = await extractKeyVocabularyFromEnglish([referenceTranslation, userTranslation]);
    const alternatives = generateAlternatives(chineseText);

    // 使用AI生成的参考翻译
    const uniqueReferenceTranslations = [referenceTranslation];
    if (alternatives.length > 0 && alternatives[0] !== referenceTranslation) {
      uniqueReferenceTranslations.push(alternatives[0]);
    }

    return {
      score: score,
      isCorrect: score >= 60,
      feedback: score >= 80 ? '翻译很好！' : score >= 60 ? '不错的翻译！' : score >= 40 ? '还可以，继续努力！' : '需要更多练习',
      suggestions: keyVocabulary.length > 0 ? keyVocabulary : [
        '注意英文语法结构',
        '可以尝试更自然的表达方式',
      ],
      referenceTranslations: uniqueReferenceTranslations,
    };
  };

  const simpleFallbackEvaluation = async (chineseText: string, userTranslation: string): Promise<AIEvaluation> => {
    const hasContent = userTranslation.length > 0;
    const isReasonableLength = userTranslation.length >= chineseText.length * 0.3;

    // 使用前端翻译作为降级方案
    const referenceTranslation = generateEnglishTranslation(chineseText);

    let score = 0;
    if (hasContent && isReasonableLength) {
      score = 50; // 基础分数

      // 检查是否包含关键英文单词
      const userLower = userTranslation.toLowerCase();
      if (userLower.includes('i') || userLower.includes('you') || userLower.includes('is') || userLower.includes('are')) {
        score += 15; // 包含基本英文词汇
      }

      // 检查长度合理性
      if (userTranslation.length >= chineseText.length * 0.5) {
        score += 10; // 长度比较合理
      }

      // 检查是否包含常见英文词汇
      const commonWords = ['the', 'a', 'an', 'to', 'and', 'or', 'but', 'in', 'on', 'at', 'with', 'for'];
      const foundCommonWords = commonWords.filter(word => userLower.includes(word));
      if (foundCommonWords.length > 0) {
        score += 10; // 使用了常见英文词汇
      }

      // 检查句子结构
      if (userTranslation.includes('.') || userTranslation.includes('!') || userTranslation.includes('?')) {
        score += 5; // 有标点符号
      }
    }

    // 限制最高分数
    score = Math.min(score, 85);

    // 获取重点词汇（从参考翻译中提取）
    const keyVocabulary = await extractKeyVocabularyFromEnglish([referenceTranslation, userTranslation]);
    const alternatives = generateAlternatives(chineseText);

    // 修复：确保参考翻译不重复
    const uniqueReferenceTranslations = [referenceTranslation];
    if (alternatives.length > 0 && alternatives[0] !== referenceTranslation) {
      uniqueReferenceTranslations.push(alternatives[0]);
    } else if (alternatives.length > 1) {
      uniqueReferenceTranslations.push(alternatives[1]);
    }

    return {
      score: score,
      isCorrect: score >= 60,
      feedback: score >= 80 ? '翻译很好！' : score >= 60 ? '不错的翻译！' : score >= 40 ? '还可以，继续努力！' : '需要更多练习',
      suggestions: keyVocabulary.length > 0 ? keyVocabulary : [
        '注意英文语法结构',
        '可以尝试更自然的表达方式',
      ],
      referenceTranslations: uniqueReferenceTranslations,
    };
  };

  // 根据难度获取分数
  const getDifficultyPoints = (difficulty: string): number => {
    switch (difficulty) {
      case 'easy': return 10;
      case 'medium': return 20;
      case 'hard': return 30;
      default: return 10;
    }
  };

  // 下一题
  const nextExercise = async () => {
    if (currentExerciseIndex < exercises.length - 1) {
      setCurrentExerciseIndex(prev => prev + 1);
      setUserAnswer('');
      setShowResult(false);
      setAiEvaluation(null);
      // 添加小延迟确保状态完全重置
      setTimeout(() => {
        console.log('进入下一题，状态已重置');
      }, 100);
    } else {
      // 练习完成
      if (isReviewMode) {
        // 复习模式 - 更新所有复习记录的状态
        console.log('🔄 复习完成，更新记忆曲线状态...');
        const updatePromises = exercises
          .filter(exercise => exercise.isReview && exercise.userAnswer)
          .map(exercise => updateReviewStatus(exercise.id, exercise.score || 0, exercise.userAnswer));
        await Promise.all(updatePromises);
      } else {
        // 常规练习 - 保存到记忆曲线系统
        await savePracticeSession();
      }
      
      if (Platform.OS === 'web') {
        // Web环境使用confirm替代Alert
        const shouldRestart = confirm(`练习完成！\n总分：${score}分\n完成：${completedExercises}/${exercises.length}题\n\n练习记录已保存，将根据记忆曲线安排复习。\n\n点击"确定"重新开始，"取消"返回聊天`);
        if (shouldRestart) {
          resetPractice();
        } else {
          onBackToChat();
        }
      } else {
        Alert.alert(
          '练习完成！',
          `总分：${score}分\n完成：${completedExercises}/${exercises.length}题\n\n练习记录已保存，将根据记忆曲线安排复习。`,
          [
            { text: '重新开始', onPress: resetPractice },
            { text: '返回', style: 'cancel', onPress: onBackToChat },
          ]
        );
      }
    }
  };

  // 保存练习会话到记忆曲线系统
  const savePracticeSession = async () => {
    try {
      console.log('💾 保存练习记录到记忆曲线系统...');
      
      // 构建练习记录数据
      const practiceData = exercises.map((exercise, index) => ({
        id: exercise.id,
        chineseText: exercise.chineseText,
        targetEnglish: exercise.targetEnglish || exercise.expectedAnswer,
        userAnswer: exercise.userAnswer || '',
        score: exercise.score || 0,
        difficulty: exercise.difficulty || 'medium',
        completedAt: new Date().toISOString(),
      }));

      // 保存到记忆曲线系统
      const sessionId = await practiceMemoryService.savePracticeSession(
        practiceData,
        undefined, // conversationId - 可以后续添加
        messages.length
      );

      console.log('✅ 练习记录保存成功:', sessionId);
      console.log('📚 记忆曲线将自动安排复习时间');
      
    } catch (error) {
      console.error('❌ 保存练习记录失败:', error);
      // 不阻塞用户流程，静默处理错误
    }
  };

  // 加载复习练习
  const loadReviewExercises = async () => {
    try {
      setIsLoadingReview(true);
      console.log('📚 加载复习练习...');
      
      const reviewData = await practiceMemoryService.generateReviewSession();
      
      if (reviewData.length === 0) {
        if (Platform.OS === 'web') {
          alert('🎉 太棒了！暂时没有需要复习的内容。\n\n继续练习新内容吧！');
        } else {
          Alert.alert('🎉 太棒了！', '暂时没有需要复习的内容。\n\n继续练习新内容吧！');
        }
        return;
      }

      // 将复习数据转换为练习格式
      const reviewExerciseData = reviewData.map((item, index) => ({
        id: item.id,
        chineseText: item.chineseText,
        targetEnglish: item.targetEnglish,
        expectedAnswer: item.expectedAnswer,
        difficulty: item.difficulty,
        points: item.points,
        isReview: true,
        originalScore: item.originalScore,
        masteryLevel: item.masteryLevel,
        reviewCount: item.reviewCount,
      }));

      console.log(`📖 加载了 ${reviewExerciseData.length} 道复习题`);
      
      setReviewExercises(reviewExerciseData);
      setExercises(reviewExerciseData);
      setIsReviewMode(true);
      setCurrentExerciseIndex(0);
      setScore(0);
      setCompletedExercises(0);
      setShowResult(false);
      setUserAnswer('');
      
    } catch (error) {
      console.error('❌ 加载复习练习失败:', error);
      if (Platform.OS === 'web') {
        alert('加载复习练习失败，请稍后重试');
      } else {
        Alert.alert('错误', '加载复习练习失败，请稍后重试');
      }
    } finally {
      setIsLoadingReview(false);
    }
  };

  // 更新复习状态
  const updateReviewStatus = async (exerciseId: string, score: number, userAnswer: string) => {
    try {
      await practiceMemoryService.updateReviewStatus(exerciseId, score, userAnswer);
      console.log('✅ 复习状态已更新');
    } catch (error) {
      console.error('❌ 更新复习状态失败:', error);
    }
  };

  // 重新开始
  const resetPractice = () => {
    setCurrentExerciseIndex(0);
    setUserAnswer('');
    setShowResult(false);
    setAiEvaluation(null);
    setScore(0);
    setCompletedExercises(0);
    setIsEvaluating(false); // 重置评估状态
    
    if (isReviewMode) {
      // 复习模式 - 重新加载复习练习
      setIsReviewMode(false);
      setReviewExercises([]);
      const newExercises = generateExercisesFromConversation(messages);
      setExercises(newExercises);
    }
    
    console.log('练习已重新开始，所有状态已重置');
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        {/* 头部导航 */}
        <View style={styles.headerContainer}>
          <TouchableOpacity style={styles.backButton} onPress={onBackToChat}>
            <Text style={styles.backButtonText}>← 返回聊天</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {isReviewMode ? '复习练习 🔄' : '翻译练习'}
          </Text>
          <TouchableOpacity 
            style={[styles.reviewButton, isLoadingReview && styles.reviewButtonDisabled]} 
            onPress={loadReviewExercises}
            disabled={isLoadingReview}
          >
            <Text style={styles.reviewButtonText}>
              {isLoadingReview ? '加载中...' : '📚 复习'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* 进度条 */}
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View
              style={[
                styles.progressFill,
                { width: `${((currentExerciseIndex + 1) / exercises.length) * 100}%` },
              ]}
            />
          </View>
          <Text style={styles.progressText}>
            {currentExerciseIndex + 1}/{exercises.length}
          </Text>
        </View>

        {/* 练习卡片 */}
        <View style={styles.exerciseCard}>
          {currentExercise && (
            <>
              {/* 题目信息 */}
              <View style={styles.exerciseHeader}>
                <View style={styles.categoryTag}>
                  <Text style={styles.categoryText}>{currentExercise.category}</Text>
                </View>
                <View style={[styles.difficultyTag, getDifficultyStyle(currentExercise.difficulty)]}>
                  <Text style={styles.difficultyText}>
                    {getDifficultyText(currentExercise.difficulty)}
                  </Text>
                </View>
              </View>

          {/* 中文题目 */}
          <View style={styles.questionContainer}>
            <Text style={styles.questionLabel}>请将以下中文翻译成英文：</Text>
            <Text style={styles.chineseText}>{currentExercise.chineseText}</Text>
          </View>

          {/* 输入框 */}
          <View style={styles.inputContainer}>
            <TextInput
              key={`input-${currentExerciseIndex}`} // 添加key确保重新渲染
              style={styles.answerInput}
              value={userAnswer}
              onChangeText={setUserAnswer}
              placeholder="请输入英文翻译..."
              placeholderTextColor="#999" // 确保占位符文字可见
              multiline
              maxLength={200}
              editable={!showResult && !isEvaluating} // 改进：评估时也禁用输入
              autoFocus={false}
              blurOnSubmit={false}
            />
          </View>

          {/* AI评估结果显示 */}
          {showResult && aiEvaluation && (
            <View style={styles.resultContainer}>
              <View style={[styles.resultHeader, aiEvaluation.isCorrect ? styles.correctResult : styles.wrongResult]}>
                <Text style={styles.resultIcon}>{aiEvaluation.isCorrect ? '✅' : '💭'}</Text>
                <Text style={styles.resultText}>
                  {aiEvaluation.feedback}
                </Text>
                <Text style={styles.scoreText}>
                  +{aiEvaluation.score}分
                </Text>
              </View>

              {/* 参考翻译 */}
              {aiEvaluation.referenceTranslations.length > 0 && (
                <View style={styles.answerContainer}>
                  <Text style={styles.correctAnswerLabel}>参考翻译：</Text>
                  {aiEvaluation.referenceTranslations.map((translation, index) => (
                    <Text key={index} style={styles.correctAnswerText}>• {translation}</Text>
                  ))}
                </View>
              )}

              {/* 重点词汇 */}
              {aiEvaluation.suggestions.length > 0 && (
                <View style={styles.tipsContainer}>
                  <Text style={styles.tipsLabel}>重点词汇：</Text>
                  {aiEvaluation.suggestions.map((suggestion, index) => (
                    <Text key={index} style={styles.tipText}>• {suggestion}</Text>
                  ))}
                </View>
              )}
            </View>
          )}

          {/* 操作按钮 */}
          <View style={styles.buttonContainer}>
            {!showResult ? (
              <TouchableOpacity
                style={[styles.checkButton, isEvaluating && styles.disabledButton]}
                onPress={checkAnswer}
                disabled={isEvaluating}
              >
                <Text style={styles.buttonText}>
                  {isEvaluating ? 'AI评估中...' : '检查答案'}
                </Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity style={styles.nextButton} onPress={nextExercise}>
                <Text style={styles.buttonText}>
                  {currentExerciseIndex < exercises.length - 1 ? '下一题' : '完成练习'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
            </>
          )}
        </View>

        {/* 分数显示 */}
        <View style={styles.scoreContainer}>
          <Text style={styles.currentScore}>当前分数: {score}</Text>
          <Text style={styles.completedCount}>已完成: {completedExercises}/{exercises.length}</Text>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

// 辅助函数
const getDifficultyStyle = (difficulty: string) => {
  switch (difficulty) {
    case 'easy': return styles.easyTag;
    case 'medium': return styles.mediumTag;
    case 'hard': return styles.hardTag;
    default: return styles.easyTag;
  }
};

const getDifficultyText = (difficulty: string) => {
  switch (difficulty) {
    case 'easy': return '简单';
    case 'medium': return '中等';
    case 'hard': return '困难';
    default: return '简单';
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollContainer: {
    padding: 16,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingHorizontal: 4,
  },
  backButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#dee2e6',
  },
  backButtonText: {
    fontSize: 14,
    color: '#6c757d',
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  reviewButton: {
    backgroundColor: '#6366f1',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  reviewButtonDisabled: {
    backgroundColor: '#9ca3af',
    opacity: 0.6,
  },
  reviewButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  progressContainer: {
    marginBottom: 16,
  },
  progressBar: {
    height: 4,
    backgroundColor: '#e0e0e0',
    borderRadius: 2,
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#4CAF50',
    borderRadius: 2,
  },
  progressText: {
    textAlign: 'center',
    fontSize: 14,
    color: '#666',
  },
  exerciseCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  exerciseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  categoryTag: {
    backgroundColor: '#E3F2FD',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
  },
  categoryText: {
    fontSize: 12,
    color: '#1976D2',
    fontWeight: '500',
  },
  difficultyTag: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
  },
  easyTag: { backgroundColor: '#E8F5E8' },
  mediumTag: { backgroundColor: '#FFF3E0' },
  hardTag: { backgroundColor: '#FFEBEE' },
  difficultyText: {
    fontSize: 12,
    fontWeight: '500',
  },
  questionContainer: {
    marginBottom: 20,
  },
  questionLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  chineseText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    lineHeight: 24,
  },
  inputContainer: {
    marginBottom: 16,
  },
  answerInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    minHeight: 80,
    textAlignVertical: 'top',
    color: '#333', // 修复：确保文字为深色，可在白色背景上清晰显示
    backgroundColor: '#fff', // 确保背景为白色
  },
  hintButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#FFC107',
    borderRadius: 16,
    marginBottom: 12,
  },
  hintButtonText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '500',
  },
  hintContainer: {
    backgroundColor: '#FFF9C4',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  hintText: {
    fontSize: 14,
    color: '#F57F17',
    marginBottom: 4,
  },
  resultContainer: {
    marginTop: 16,
  },
  resultHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  correctResult: { backgroundColor: '#E8F5E8' },
  wrongResult: { backgroundColor: '#FFEBEE' },
  resultIcon: {
    fontSize: 20,
    marginRight: 8,
  },
  resultText: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
  scoreText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4CAF50',
  },
  answerContainer: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  correctAnswerLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  correctAnswerText: {
    fontSize: 16,
    color: '#4CAF50',
    marginBottom: 8,
  },
  alternativesLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginTop: 8,
    marginBottom: 4,
  },
  alternativeText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 2,
  },
  tipsContainer: {
    backgroundColor: '#E3F2FD',
    padding: 12,
    borderRadius: 8,
  },
  tipsLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1976D2',
    marginBottom: 8,
  },
  tipText: {
    fontSize: 14,
    color: '#1976D2',
    marginBottom: 4,
  },
  buttonContainer: {
    marginTop: 20,
  },
  checkButton: {
    backgroundColor: '#2196F3',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  nextButton: {
    backgroundColor: '#4CAF50',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  disabledButton: {
    backgroundColor: '#bdc3c7',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  scoreContainer: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  currentScore: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4CAF50',
  },
  completedCount: {
    fontSize: 14,
    color: '#666',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#4CAF50',
    marginBottom: 8,
  },
  loadingSubtext: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
});

export default TranslationPracticeScreen;
